import * as React from "react";
import { FC, useEffect } from "react";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import InlineLoading from "src/components/common/InlineLoading/InlineLoading";
import { DashboardBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useDispatch, { useActionCreator } from "src/hooks/useDispatch";
import useNavigate from "src/hooks/useNavigate";
import useSelector from "src/hooks/useSelector";
import * as NavigationActions from "../../actions/navigation";
import * as PagingActions from "../../actions/pages/pagingPage";
import * as TrialInstitutionsActions from "../../actions/trialInstitution";
import { FormFilterInput } from "../../components/form";
import {
    RestrictedLayout,
    TruncatedCell,
} from "../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../constants/reactTableConstants";
import * as Dtos from "../../dtos/Tmd.dtos";
import { RequestState } from "../../enumerations/RequestState";
import * as AuthenticationHelper from "../../helpers/authentication";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../helpers/date";
import { findLookupValue } from "../../helpers/lookupHelper";

import "./DashboardPage.scss";

interface IDashboardTrialInstitutionFormsProps {
    trialInstitutions: Dtos.TrialInstitution[];
}

const DashboardTrialInstitutionForms: FC<
    IDashboardTrialInstitutionFormsProps
> = ({ trialInstitutions }) => {
    const navigate = useNavigate();
    const permissions = useSelector(
        (state) => state.user.userSummary?.permissions ?? []
    );
    const pageSize = useSelector((state) => state.pagingPage.pageSize);
    const lookups = useSelector((state) => state.trialInstitution.lookups);

    const dispatch = useDispatch();
    const setPageSize = useActionCreator(
        PagingActions.PageSizeChanged,
        dispatch
    );

    if (!trialInstitutions || trialInstitutions.length == 0) {
        return (
            <div className="card card-shadow p-3">
                <div className="alert alert-info d-flex mb-0">
                    <div className="mr-2">
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                            fixedWidth
                        />
                    </div>
                    <span>There are currently no trial institutions</span>
                </div>
            </div>
        );
    }

    const showPagination = trialInstitutions.length > pageSize;

    return (
        <div className="table-wrapper">
            <ReactTable
                data={trialInstitutions}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                defaultPageSize={
                    showPagination ? pageSize : trialInstitutions.length
                }
                pageSize={showPagination ? pageSize : trialInstitutions.length}
                showPagination={showPagination}
                className="-striped -highlight -clickable"
                noDataText="There are currently no trial groups to display"
                resizable={false}
                onPageSizeChange={(val) => setPageSize(val)}
                getTdProps={(_state, rowInfo: RowInfo, column, _instance) => {
                    return {
                        onClick: (e, handleOriginal) => {
                            if (
                                column.id == "personnelCount" &&
                                AuthenticationHelper.hasPermission(
                                    permissions,
                                    Dtos.Permission.TrialPersonnelRoleView,
                                    rowInfo.original.trialId,
                                    rowInfo.original.institutionId
                                )
                            ) {
                                const url: string =
                                    "/trials/" +
                                    rowInfo.original.trialId +
                                    "/trialinstitution/" +
                                    rowInfo.original.id +
                                    "/personnel";
                                navigate(url);
                            } else if (
                                AuthenticationHelper.hasPermission(
                                    permissions,
                                    Dtos.Permission.TrialInstitutionView,
                                    rowInfo.original.trialId,
                                    rowInfo.original.institutionId
                                )
                            ) {
                                const url: string =
                                    "/trials/" +
                                    rowInfo.original.trialId +
                                    "/trialinstitution/" +
                                    rowInfo.original.id +
                                    "/details";
                                navigate(url);
                            }

                            if (handleOriginal) {
                                handleOriginal();
                            }
                        },
                    };
                }}
                columns={[
                    {
                        id: "id",
                        Header: " ",
                        accessor: "id",
                        maxWidth: 50,
                        Cell: (_props) => (
                            <div className="large-small-icon">
                                <FontAwesomeIcon
                                    className="large-icon"
                                    icon={FontAwesomeIcons.Light.TRIAL}
                                    fixedWidth
                                />
                                <FontAwesomeIcon
                                    className="small-icon"
                                    icon={FontAwesomeIcons.Light.INSTITUTION}
                                    fixedWidth
                                />
                            </div>
                        ),
                    },
                    {
                        id: "protocolID",
                        Header: "BCT Number",
                        accessor: "trial.protocolID",
                        maxWidth: 180,
                    },
                    /*{
                    id: "title",
                    Header: 'Scientific Title',
                    accessor: 'trial.title',
                    className: "d-flex align-items-left justify-content-left",
                    headerClassName: "text-left",
                    Cell: (props) => <TruncatedCell value={props.value} />
                }, */
                    {
                        id: "shortTitle",
                        Header: "Short Title",
                        accessor: "trial.shortTitle",
                        maxWidth: 280,
                        Cell: (props) => <TruncatedCell value={props.value} />,
                    },
                    {
                        id: "name",
                        Header: "Institution",
                        accessor: "institution.name",
                        Cell: (props) => <TruncatedCell value={props.value} />,
                    },
                    {
                        id: "trialInstitutionStatusId",
                        Header: "Status",
                        accessor: "trialInstitutionStatusId",
                        Cell: (props) => (
                            <div>
                                <span
                                    data-tip={convertToApproximateDateString(
                                        props.value
                                    )}
                                >
                                    {findLookupValue(
                                        "TrialInstitution",
                                        "TrialInstitutionStatusId",
                                        props.value,
                                        lookups
                                    )}
                                </span>
                                <ReactTooltip />
                            </div>
                        ),
                    },
                    {
                        id: "personnelCount",
                        Header: "Roles",
                        accessor: "personnelCount",
                        maxWidth: 80,
                        Cell: (props) => (
                            <div>
                                <span className="text-info">
                                    <u>{props.value || "0"}</u>
                                </span>
                            </div>
                        ),
                    },
                    {
                        id: "dateActivated",
                        Header: "Date Activated",
                        accessor: "dateActivated",
                        Cell: (props) => (
                            <div>
                                <span
                                    data-tip={convertToApproximateDateString(
                                        props.value
                                    )}
                                >
                                    {convertToShortDateString(props.value)}
                                </span>
                                <ReactTooltip />
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
};

const DashboardTrials: FC = () => {
    const trials = useSelector((state) => {
        const trialInstitutionsLoaded =
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success;
        const trialInstitutionForms = state.trialInstitution.formData;

        if (!trialInstitutionsLoaded) {
            return undefined;
        }

        if (
            trialInstitutionForms instanceof Array &&
            trialInstitutionForms.length > 0
        ) {
            return trialInstitutionForms;
        }

        return [];
    });

    if (!trials) {
        return <InlineLoading absoluteCenter={true} size={40} />;
    }

    return (
        <div className="card">
            <div className="patient-tables">
                <DashboardTrialInstitutionForms trialInstitutions={trials} />
            </div>
        </div>
    );
};

const DashboardContent: FC = () => {
    const formFilter = useSelector(
        (state) => state.trialInstitution.formFilter
    );

    const dispatch = useDispatch();
    const filter = useActionCreator(TrialInstitutionsActions.Filter, dispatch);

    return (
        <div>
            <div className="row">
                <div className={"col-auto mb-2"}>
                    <h1>My Trial Institutions</h1>
                </div>
                <div
                    className="col d-flex justify-content-end align-items-stretch"
                    style={{ height: "38px" }}
                >
                    <FormFilterInput
                        formFilter={formFilter}
                        onFilter={(e) => filter(e)}
                        hideActiveToggle={true}
                    />
                </div>
            </div>
            <DashboardTrials />
        </div>
    );
};

const DashboardPage: FC = () => {
    const loading = useSelector((state) => {
        const loadingTrialInstitutions =
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending;

        return loadingTrialInstitutions;
    });

    const dispatch = useDispatch();
    const loadTrialInstitutions = useActionCreator(
        TrialInstitutionsActions.LoadTrialInstitution,
        dispatch
    );
    const clearTrialInstitutions = useActionCreator(
        TrialInstitutionsActions.Clear,
        dispatch
    );
    const setDrawerItems = useActionCreator(
        NavigationActions.SetDrawerItems,
        dispatch
    );
    const setBreadcrumbs = useActionCreator(
        NavigationActions.SetBreadcrumbs,
        dispatch
    );


    useEffect(() => {
        setDrawerItems([]);
        setBreadcrumbs(DashboardBreadcrumbs());
        loadTrialInstitutions();

        return () => {
            clearTrialInstitutions();
        };
    }, [
        loadTrialInstitutions,
        clearTrialInstitutions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    return (
        <RestrictedLayout
            anyMatchpermission={Dtos.Permission.DashboardView}
            loading={loading}
        >
            <DashboardContent />
        </RestrictedLayout>
    );
};

export default DashboardPage;
